// frontend/src/pages/storeActivity/TimelinePanel.jsx
//
// The newest events, one per line. Setup answers and approvals read here
// inline (`timelineDetail`); any other row shows the backend's plain detail.
//
// Scheduled work that went as planned is hidden by default: at one row per
// pipeline per night it buries what people did under what the scheduler did.
// It is one click away for the reader who wants "my numbers changed
// overnight" answered. Scheduled work that did NOT go as planned — a failed
// run or synchronization, a requeued job — is always shown.
//
// A scheduled run opens to its steps: which ran, which skipped and why,
// which failed, and what each processed.
//
// An action the server refused (`outcome: 'error'`, core/activity_tracking.py)
// is a row like any other — the merchant did try — and reads as failed.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ICON, ClockCounterClockwise } from '../../icons';
import { useFormatters } from '../../hooks/useFormatters';
import { Panel, SourceChip } from './primitives';
import { eventLabel, isRoutineAutomation, stepLabel } from './text';
import { timelineDetail } from './timelineDetail';

export default function TimelinePanel({ store, timeline, truncated }) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  const [withSystem, setWithSystem] = useState(false);

  // The uninstall is a tracked event, so the timeline usually carries it. A
  // store that churned before `app_uninstalled` was tracked has no such row;
  // the synthetic marker says why its timeline stops.
  const hasUninstallEvent = timeline.some((e) => e.event_type === 'app_uninstalled');
  const rows = [];
  if (store.churned && store.uninstalled_at && !hasUninstallEvent) {
    rows.push({
      when: store.uninstalled_at, churn: true, category: 'lifecycle',
      event_type: 'app_uninstalled', detail: '', properties: {}, source: 'system',
    });
  }
  rows.push(...timeline.map(
    (e) => (e.event_type === 'app_uninstalled' ? { ...e, churn: true } : e),
  ));
  const visible = withSystem ? rows : rows.filter((e) => !isRoutineAutomation(e));

  return (
    <Panel
      icon={<ClockCounterClockwise size={ICON.md} />}
      title={t('storeActivity.timeline.title')}
      subtitle={t('storeActivity.timeline.subtitle')}
      actions={(
        <label className="sa-toggle">
          <input type="checkbox" checked={withSystem} onChange={(e) => setWithSystem(e.target.checked)} />
          {t('storeActivity.timeline.showSystem')}
        </label>
      )}
    >
      {visible.length === 0
        ? <div className="sa-panel-empty">{t('storeActivity.timeline.empty')}</div>
        : (
          <div className="sa-timeline">
            {visible.map((e, i) => {
              const detail = timelineDetail(e, t);
              const failed = e.properties.outcome === 'error';
              const problem = failed || (e.category === 'automation' && !isRoutineAutomation(e));
              return (
                <div
                  className={`sa-tl${e.churn ? ' sa-tl--churn' : ''}${problem ? ' sa-tl--problem' : ''}`}
                  key={i}
                >
                  <span className="sa-tl-time">{formatDateTime(e.when)}</span>
                  <span className="sa-tl-rail">
                    <span className={`sa-tl-dot sa-tl-dot--${e.churn ? 'churn' : e.source}`} />
                  </span>
                  {/* The chip, not just the dot. Colour alone made Embedded vs
                      SaaS a legend nobody had, in two hues a page of them does
                      not separate. */}
                  <SourceChip source={e.source} />
                  <span className="sa-tl-body">
                    <span className="sa-tl-event">{eventLabel(e.event_type, t)}</span>
                    {failed && <span className="sa-chip sa-chip--failed">{t('storeActivity.timeline.failed')}</span>}
                    {detail ? <span className="sa-tl-detail"> · {detail}</span> : null}
                    {e.event_type === 'scheduled_job_run' && e.properties.steps && (
                      <StepList steps={e.properties.steps} />
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      {truncated && (
        <div className="sa-panel-note">{t('storeActivity.truncated.timeline')}</div>
      )}
    </Panel>
  );
}

/** The steps of one scheduled run, collapsed under a count. */
function StepList({ steps }) {
  const { t } = useTranslation();
  return (
    <details className="sa-tl-steps">
      <summary>{t('storeActivity.automation.stepsSummary', { count: steps.length })}</summary>
      <ul>
        {steps.map((s) => {
          const counts = Object.entries(s.counts).map(([key, value]) => `${key} ${value}`).join(', ');
          return (
            <li key={s.step} className={`sa-tl-step sa-tl-step--${s.status}`}>
              <span className="sa-tl-step-name">{stepLabel(s.step, t)}</span>
              {` · ${t(`storeActivity.stepStatus.${s.status}`)}`}
              {s.note ? <span className="sa-tl-step-note"> · {s.note}</span> : null}
              {counts ? <span className="sa-tl-step-counts"> · {counts}</span> : null}
            </li>
          );
        })}
      </ul>
    </details>
  );
}
